import { useState, startTransition } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PropertyForm } from "@/components/host/properties/PropertyForm";
import { AddPropertyHeader } from "@/components/host/properties/AddPropertyHeader";

const AddPropertyPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (formData: any) => {
    setIsLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error: propertyError } = await supabase
        .from("properties")
        .insert({
          host_id: user.id,
          title: formData.title,
          description: formData.description,
          price_per_night: parseFloat(formData.price_per_night),
          bedrooms: parseInt(formData.bedrooms),
          bathrooms: parseInt(formData.bathrooms),
          max_guests: parseInt(formData.max_guests),
          address: formData.address,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          property_type: formData.property_type,
          images: formData.images,
          virtual_tour_url: formData.virtual_tour_url,
        });

      if (propertyError) throw propertyError;

      // Update onboarding progress
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          host_onboarding_step: 5,
          host_onboarding_data: {
            properties: true,
            verification: true,
            profile: true,
            payment: true,
          },
        })
        .eq("id", user.id);

      if (profileError) throw profileError;

      toast({
        title: "Success",
        description: "Property added successfully",
      });

      startTransition(() => {
        navigate("/host/properties");
      });
    } catch (error: any) {
      console.error("Error adding property:", error);
      toast({
        title: "Error",
        description: "Failed to add property. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <AddPropertyHeader />
      <Card className="p-6">
        <PropertyForm
          onSubmit={handleSubmit}
          onCancel={() => {
            startTransition(() => {
              navigate("/host/properties");
            });
          }}
          isLoading={isLoading}
        />
      </Card>
    </div>
  );
};

export default AddPropertyPage;